// gap-check-questions.ts
import { GapCheckSection } from '../../shared/models/gap-check-section';

// ============================================================
// ISO 27001:2022 Questions
// ============================================================
export const ISO_27001_QUESTIONS: GapCheckSection[] = [
  {
    title: 'Context of the Organisation',
    target: 8,
    questions: [
      {
        id: '4-01',
        type: 'radio',
        label: 'Has the organisation established and implemented an ISMS?',
        value: '',
        clauseRef: '4.4',
        options: ['Yes', 'No', 'Partial'],
        expected: 'A formal ISMS must be established, implemented, maintained, and continually improved, including defined processes, governance, and documentation aligned to ISO 27001.',
      },
      {
        id: '4-02',
        type: 'radio',
        label: 'Has the organisation identified internal and external factors that could affect its information security?',
        value: '',
        clauseRef: '4.1',
        options: ['Yes', 'No', 'Partial'],
        expected: 'The organisation must identify and document internal and external issues (e.g. regulatory, operational, technological) that could impact information security objectives.',
      },
      {
        id: '4-03',
        type: 'radio',
        label: 'Has the organisation identified relevant interested parties and their information security requirements?',
        value: '',
        clauseRef: '4.2',
        options: ['Yes', 'No', 'Partial'],
        expected: 'A documented list of stakeholders (e.g. customers, regulators, suppliers) and their information security requirements must be maintained and reviewed.',
      },
      {
        id: '4-04',
        type: 'radio',
        label: 'Has the organisation defined the scope of its ISMS?',
        value: '',
        clauseRef: '4.3',
        options: ['Yes', 'No', 'Partial'],
        expected: 'The ISMS scope must clearly define boundaries, including business units, systems, services, and locations covered.',
      },
      {
        id: '4-05',
        type: 'radio',
        label: 'Is the scope of the ISMS documented and available?',
        value: '',
        clauseRef: '4.3',
        options: ['Yes', 'No', 'Partial'],
        expected: 'The ISMS scope must be formally documented, approved, and available to relevant stakeholders.',
      },
    ],
  },
  {
    title: 'Leadership',
    target: 8,
    questions: [
      {
        id: "5-01",
        type: "radio",
        label: "Does top management take accountability for and support the Organisation's ISMS?",
        value: "",
        clauseRef: "5.1",
        options: ["Yes", "No", "Partial"],
        expected: "Top management must demonstrate leadership and accountability for the effectiveness of the ISMS."
      },
      {
        id: "5-02",
        type: "radio",
        label: "Does the Organisation have a documented information security policy and is it formally approved?",
        value: "",
        clauseRef: "5.2",
        options: ["Yes", "No", "Partial"],
        expected: "A formal information security policy must be documented, approved by management, and aligned to business objectives."
      },
      {
        id: "5-03",
        type: "radio",
        label: "Are sufficient resources (people, time, tools, budget) available?",
        value: "",
        clauseRef: "5.1, 7.1",
        options: ["Yes", "No", "Partial"],
        expected: "Adequate resources must be allocated to establish, implement, and maintain the ISMS."
      },
      {
        id: "5-04",
        type: "radio",
        label: "Is the information security policy communicated internally and to stakeholders?",
        value: "",
        clauseRef: "5.2",
        options: ["Yes", "No", "Partial"],
        expected: "The policy must be communicated internally and made available to relevant stakeholders."
      },
      {
        id: "5-05",
        type: "radio",
        label: "Are ISMS roles and responsibilities clearly defined and assigned?",
        value: "",
        clauseRef: "5.3",
        options: ["Yes", "No", "Partial"],
        expected: "Roles and responsibilities must be clearly defined, assigned, and communicated."
      }
    ]
  },
  {
    title: 'Planning',
    target: 8,
    questions: [
      {
        id: '6-01',
        type: 'radio',
        label: 'Is there a defined information security risk assessment process?',
        value: '',
        clauseRef: '6.1',
        options: ['Yes', 'No', 'Partial'],
        expected: 'A documented risk assessment methodology must be established, including criteria for risk evaluation.',
      },
      {
        id: '6-02',
        type: 'radio',
        label: 'Has an information security risk assessment been performed?',
        value: '',
        clauseRef: '6.1',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Formal risk assessments must be conducted covering all assets within scope.',
      },
      {
        id: '6-03',
        type: 'radio',
        label: 'Are risk owners assigned and responsible for risk decisions?',
        value: '',
        clauseRef: '6.1',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Risk owners must be assigned and responsible for approving treatment and accepting residual risks.',
      },
      {
        id: '6-04',
        type: 'radio',
        label: 'Are actions defined to address identified risks?',
        value: '',
        clauseRef: '6.1',
        options: ['Yes', 'No', 'Partial'],
        expected: 'A structured risk treatment plan must be defined and maintained.',
      },
      {
        id: '6-05',
        type: 'radio',
        label: 'Is a Statement of Applicability (SoA) defined?',
        value: '',
        clauseRef: '6.1',
        options: ['Yes', 'No', 'Partial'],
        expected: 'The SoA must document selected controls, justification, and implementation status.',
      },
      {
        id: '6-06',
        type: 'radio',
        label: 'Are information security objectives defined and documented?',
        value: '',
        clauseRef: '6.2',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Measurable information security objectives must be defined and aligned to business goals.',
      },
      {
        id: '6-07',
        type: 'radio',
        label: 'Are objectives monitored and reviewed?',
        value: '',
        clauseRef: '6.2',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Objectives must be regularly monitored, measured, and reviewed.',
      },
      {
        id: '6-08',
        type: 'radio',
        label: 'Are ISMS changes planned and controlled?',
        value: '',
        clauseRef: '6.3',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Changes to the ISMS must be managed in a controlled and documented manner.',
      },
    ],
  },
  {
    title: 'Support',
    target: 8,
    questions: [
      {
        id: '7-01',
        type: 'radio',
        label: 'Are personnel competent for ISMS roles?',
        value: '',
        clauseRef: '7.2',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Personnel must be competent and supported by appropriate training and records.'
      },
      {
        id: '7-02',
        type: 'radio',
        label: 'Are employees aware of information security policy?',
        value: '',
        clauseRef: '7.3',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Employees must be aware of the policy and their responsibilities for protecting information.'
      },
      {
        id: '7-03',
        type: 'radio',
        label: 'Is ISMS documentation maintained?',
        value: '',
        clauseRef: '7.5',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Required documented information must be created, maintained, and controlled.'
      },
      {
        id: '7-04',
        type: 'radio',
        label: 'Is there a communication process for security matters?',
        value: '',
        clauseRef: '7.4',
        options: ['Yes', 'No', 'Partial'],
        expected: 'A structured communication process must be established for internal and external stakeholders.'
      },
      {
        id: '7-05',
        type: 'radio',
        label: 'Is documentation properly controlled and protected?',
        value: '',
        clauseRef: '7.5',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Documentation must be controlled (e.g. versioning, access, protection).'
      }
    ]
  },
  {
    title: 'Operation',
    target: 8,
    questions: [
      {
        id: '8-01',
        type: 'radio',
        label: 'Are ISMS operational processes implemented?',
        value: '',
        clauseRef: '8.1',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Operational processes must be defined and implemented to manage information security risks.'
      },
      {
        id: '8-02',
        type: 'radio',
        label: 'Is risk assessment reviewed periodically?',
        value: '',
        clauseRef: '8.2',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Risk assessments must be reviewed regularly or when significant changes occur.'
      },
      {
        id: '8-03',
        type: 'radio',
        label: 'Are risk treatment actions implemented?',
        value: '',
        clauseRef: '8.3',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Risk treatment actions must be implemented and tracked.'
      }
    ]
  },
  {
    title: 'Performance Evaluation',
    target: 8,
    questions: [
      {
        id: '9-01',
        type: 'radio',
        label: 'Is ISMS performance monitored and measured?',
        value: '',
        clauseRef: '9.1',
        options: ['Yes', 'No', 'Partial'],
        expected: 'The organisation must define and implement monitoring and measurement of ISMS effectiveness.'
      },
      {
        id: '9-02',
        type: 'radio',
        label: 'Are internal audits conducted at planned intervals?',
        value: '',
        clauseRef: '9.2',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Internal audits must be conducted by objective and independent personnel.'
      },
      {
        id: '9-03',
        type: 'radio',
        label: 'Are management reviews conducted periodically?',
        value: '',
        clauseRef: '9.3',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Top management must conduct regular ISMS management reviews.'
      }
    ]
  },
  {
    title: 'Improvement',
    target: 8,
    questions: [
      {
        id: '10-01',
        type: 'radio',
        label: 'Are improvements identified and implemented?',
        value: '',
        clauseRef: '10.1',
        options: ['Yes', 'No', 'Partial'],
        expected: 'The organisation must continually improve the ISMS based on performance, audits, and incidents.'
      },
      {
        id: '10-02',
        type: 'radio',
        label: 'Is there a corrective action process?',
        value: '',
        clauseRef: '10.2',
        options: ['Yes', 'No', 'Partial'],
        expected: 'A formal corrective action process must be established to address and prevent recurrence of issues.'
      }
    ]
  },
  { title: 'Summary', questions: [] },
];

// ============================================================
// ISO 42001:2023 – AI Management System (Detailed)
// ============================================================
export const ISO_42001_QUESTIONS: GapCheckSection[] = [
  {
    title: 'Context of the Organisation',
    target: 8,
    questions: [
      {
        id: '4-01-AI',
        type: 'radio',
        label: 'Has the organisation established and implemented an AI Management System (AIMS)?',
        value: '',
        clauseRef: '4.1',
        options: ['Yes', 'No', 'Partial'],
        expected: 'The organisation must establish, implement, maintain and continually improve an AI Management System in accordance with ISO/IEC 42001.',
      },
      {
        id: '4-02-AI',
        type: 'radio',
        label: 'Has the organisation identified internal and external factors relevant to its AI activities (e.g., its role as an AI developer, provider or user, and applicable regulations such as the EU AI Act)?',
        value: '',
        clauseRef: '4.1',
        options: ['Yes', 'No', 'Partial'],
        expected: 'The organisation must identify and document internal and external issues (e.g. regulatory, operational, technological) that could impact AI management objectives.',
      },
      {
        id: '4-03-AI',
        type: 'radio',
        label: 'Has the organisation identified relevant interested parties (e.g., customers, regulators, data subjects, suppliers) and their requirements relating to AI?',
        value: '',
        clauseRef: '4.2',
        options: ['Yes', 'No', 'Partial'],
        expected: 'A documented list of stakeholders and their AI-related requirements must be maintained and reviewed.',
      },
      {
        id: '4-04-AI',
        type: 'radio',
        label: 'Has the organisation defined the scope of its AIMS (e.g., which AI systems, business units, services or use cases are included)?',
        value: '',
        clauseRef: '4.3',
        options: ['Yes', 'No', 'Partial'],
        expected: 'The AIMS scope must clearly define boundaries, including AI systems, business units, services and use cases covered.',
      },
      {
        id: '4-05-AI',
        type: 'radio',
        label: 'Is the scope of the AIMS documented and available?',
        value: '',
        clauseRef: '4.3',
        options: ['Yes', 'No', 'Partial'],
        expected: 'The AIMS scope must be formally documented, approved, and available to relevant stakeholders.',
      },
    ],
  },
  {
    title: 'Leadership',
    target: 8,
    questions: [
      {
        id: '5-01-AI',
        type: 'radio',
        label: 'Does top management take accountability for and support the organisation\'s AIMS?',
        value: '',
        clauseRef: '5.1',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Top management must demonstrate leadership and accountability for the effectiveness of the AIMS.',
      },
      {
        id: '5-02-AI',
        type: 'radio',
        label: 'Does the organisation have a documented AI policy that is formally approved by top management?',
        value: '',
        clauseRef: '5.2',
        options: ['Yes', 'No', 'Partial'],
        expected: 'A formal AI policy must be documented, approved by top management, and aligned with the organisation\'s strategic objectives.',
      },
      {
        id: '5-03-AI',
        type: 'radio',
        label: 'Are sufficient resources (people, skills, tools, compute, budget) available to operate and maintain the AIMS?',
        value: '',
        clauseRef: '5.1, 7.1',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Adequate resources must be allocated to establish, implement, maintain and improve the AIMS.',
      },
      {
        id: '5-04-AI',
        type: 'radio',
        label: 'Is the AI policy communicated within the organisation and available to relevant stakeholders?',
        value: '',
        clauseRef: '5.2',
        options: ['Yes', 'No', 'Partial'],
        expected: 'The AI policy must be communicated internally and made available to relevant stakeholders.',
      },
      {
        id: '5-05-AI',
        type: 'radio',
        label: 'Are AIMS roles, responsibilities and authorities clearly defined and assigned (including accountability for AI risk, oversight and data)?',
        value: '',
        clauseRef: '5.3',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Roles, responsibilities and authorities for AI governance must be defined, assigned and communicated.',
      },
    ],
  },
  {
    title: 'Planning',
    target: 8,
    questions: [
      {
        id: '6-01-AI',
        type: 'radio',
        label: 'Is there a defined AI risk assessment process to support the AIMS implementation?',
        value: '',
        clauseRef: '6.1',
        options: ['Yes', 'No', 'Partial'],
        expected: 'A documented AI risk assessment methodology must be established, including risk criteria and evaluation.',
      },
      {
        id: '6-02-AI',
        type: 'radio',
        label: 'Has the organisation performed an AI risk assessment to identify and evaluate risks within the AIMS scope?',
        value: '',
        clauseRef: '6.1',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Formal AI risk assessments must be conducted covering all AI systems and activities within scope.',
      },
      {
        id: '6-03-AI',
        type: 'radio',
        label: 'Are AI risk owners assigned and responsible for approving risk treatment actions and formally accepting any remaining (residual) AI risks?',
        value: '',
        clauseRef: '6.1',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Risk owners must be assigned and responsible for approving treatment and accepting residual risks.',
      },
      {
        id: '6-04-AI',
        type: 'radio',
        label: 'Does the organisation define and plan actions to address identified AI risks (e.g., mitigation, controls, risk acceptance, or other measures)?',
        value: '',
        clauseRef: '6.1',
        options: ['Yes', 'No', 'Partial'],
        expected: 'A structured risk treatment plan must be defined and maintained.',
      },
      {
        id: '6-05-AI',
        type: 'radio',
        label: 'Has the organisation defined controls for AI risk treatment and documented them in a Statement of Applicability (SoA)?',
        value: '',
        clauseRef: '6.1',
        options: ['Yes', 'No', 'Partial'],
        expected: 'The SoA must document selected controls, justification, and implementation status.',
      },
      {
        id: '6-06-AI',
        type: 'radio',
        label: 'Has the organisation defined and documented AI objectives (e.g., fairness, transparency, safety, accountability)?',
        value: '',
        clauseRef: '6.2',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Measurable AI objectives must be defined, aligned with the AI policy, and documented.',
      },
      {
        id: '6-07-AI',
        type: 'radio',
        label: 'Are these AI objectives monitored and reviewed?',
        value: '',
        clauseRef: '6.2',
        options: ['Yes', 'No', 'Partial'],
        expected: 'AI objectives must be regularly monitored, measured, and reviewed for effectiveness.',
      },
      {
        id: '6-08-AI',
        type: 'radio',
        label: 'Are changes to the AIMS planned and managed in a controlled manner?',
        value: '',
        clauseRef: '6.3',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Changes to the AIMS must be planned, documented and managed in a controlled manner.',
      },
    ],
  },
  {
    title: 'Support',
    target: 8,
    questions: [
      {
        id: '7-01-AI',
        type: 'radio',
        label: 'Are individuals performing AIMS roles competent based on appropriate training, skills, or experience (including AI and machine-learning expertise)?',
        value: '',
        clauseRef: '7.2',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Personnel must be competent and supported by appropriate training, education, or experience in AI and ML.',
      },
      {
        id: '7-02-AI',
        type: 'radio',
        label: 'Are employees aware of the AI policy and their responsibilities for the responsible development and use of AI?',
        value: '',
        clauseRef: '7.3',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Employees must be aware of the AI policy, their responsibilities, and the implications of AI on the organisation.',
      },
      {
        id: '7-03-AI',
        type: 'radio',
        label: 'Has the organisation established and maintained the documented information required for the AIMS?',
        value: '',
        clauseRef: '7.5',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Required documented information must be created, maintained and controlled.',
      },
      {
        id: '7-04-AI',
        type: 'radio',
        label: 'Is there a defined process for communicating AI matters internally and externally when necessary?',
        value: '',
        clauseRef: '7.4',
        options: ['Yes', 'No', 'Partial'],
        expected: 'A structured communication process must be established for internal and external stakeholders on AI matters.',
      },
      {
        id: '7-05-AI',
        type: 'radio',
        label: 'Is AIMS documented information properly controlled and protected (e.g., version control, restricted access)?',
        value: '',
        clauseRef: '7.5',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Documentation must be controlled (versioning, access, protection) to ensure integrity and availability.',
      },
    ],
  },
  {
    title: 'Operation',
    target: 8,
    questions: [
      {
        id: '8-01-AI',
        type: 'radio',
        label: 'Does the organisation have processes in place to support the implementation of the AIMS?',
        value: '',
        clauseRef: '8.1',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Operational processes must be defined and implemented to manage AI risks and achieve AIMS objectives.',
      },
      {
        id: '8-02-AI',
        type: 'radio',
        label: 'Is the AI risk assessment reviewed and updated periodically or when significant changes occur?',
        value: '',
        clauseRef: '8.2',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Risk assessments must be reviewed regularly or when significant changes occur.',
      },
      {
        id: '8-03-AI',
        type: 'radio',
        label: 'Are AI risk treatment actions implemented to address identified AI risks?',
        value: '',
        clauseRef: '8.3',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Risk treatment actions must be implemented and tracked to completion.',
      },
      {
        id: '8-04-AI',
        type: 'radio',
        label: 'Does the organisation conduct AI system impact assessments to evaluate impacts on individuals, groups and society?',
        value: '',
        clauseRef: '8.4',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Impact assessments must be conducted to evaluate the societal and individual impacts of AI systems.',
      },
    ],
  },
  {
    title: 'Performance Evaluation',
    target: 8,
    questions: [
      {
        id: '9-01-AI',
        type: 'radio',
        label: 'Has the organisation defined how the performance and effectiveness of the AIMS will be monitored and measured?',
        value: '',
        clauseRef: '9.1',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Monitoring and measurement criteria for AIMS performance must be defined.',
      },
      {
        id: '9-02-AI',
        type: 'radio',
        label: 'Does the organisation conduct internal audits at planned intervals against the requirements of ISO/IEC 42001:2023, using auditors who are objective and independent of the activities being audited?',
        value: '',
        clauseRef: '9.2',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Internal audits must be conducted by competent, independent auditors.',
      },
      {
        id: '9-03-AI',
        type: 'radio',
        label: 'Does top management conduct periodic management reviews of the AIMS?',
        value: '',
        clauseRef: '9.3',
        options: ['Yes', 'No', 'Partial'],
        expected: 'Top management must conduct regular management reviews of the AIMS.',
      },
    ],
  },
  {
    title: 'Improvement',
    target: 8,
    questions: [
      {
        id: '10-01-AI',
        type: 'radio',
        label: 'Does the organisation identify and implement improvements to the AIMS based on monitoring results, audits, incidents, or management review outcomes?',
        value: '',
        clauseRef: '10.1',
        options: ['Yes', 'No', 'Partial'],
        expected: 'The organisation must continually improve the AIMS based on performance, audits, incidents and reviews.',
      },
      {
        id: '10-02-AI',
        type: 'radio',
        label: 'Does the organisation have a process to manage and document nonconformities and take corrective actions to prevent recurrence?',
        value: '',
        clauseRef: '10.2',
        options: ['Yes', 'No', 'Partial'],
        expected: 'A formal corrective action process must be established to address nonconformities and prevent recurrence.',
      },
    ],
  },
  { title: 'Summary', questions: [] },
];